import CopyPasteProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/CopyPasteProductPricing'
import SpecialZonePricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/SpecialZonePricing'
import StyledDataGrid, {
  DATA_GRID_BORDER_WIDTH
} from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/StyledProductPricingGrid'
import type { RowType } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/types'
import {
  type PricingPlan,
  PricingTabType,
  type Product,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import { EXTENSION_TICKET_GROUP_CODE, IS_TARIFF_EDIT_ALLOWED } from 'Util/constants'
import { calculateISODurationLengthInMinutes, createDurationMap } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, LinearProgress, Skeleton } from '@mui/material'
import { type GridCellParams, type GridColDef, useGridApiRef } from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { t } from 'ttag'

const TITLE_COL_WIDTH = 50
const ROW_HEIGHT = 40
const COL_WIDTH = 150

const IsCellEditedClassName = 'IsEdited'
const IsCellUpdatedClassName = 'IsUpdated'

function CustomNoRowsOverlay(rows: Readonly<string[]>) {
  return (
    <>
      <LinearProgress />
      {rows.map(row => (
        <Skeleton key={`skeleton-row-${row}`} height={ROW_HEIGHT} animation="wave" />
      ))}
    </>
  )
}

/**
 * Generates a unique title for a pricing plan based on its properties. Pricings and title
 * are excluded from the generated title.
 */
const generatePricingPlanTitle = (pricingPlan: PricingPlan, productId: string) => {
  const {
    active_period,
    custom_fields,
    description,
    domicile_class,
    visible_period,
    usage_validity_duration,
    vat_code,
    zone_map
  } = pricingPlan

  return `${productId}-${JSON.stringify(active_period)}-${JSON.stringify(
    custom_fields
  )}-${description}-${domicile_class}-${JSON.stringify(
    visible_period
  )}-${usage_validity_duration}-${vat_code}-${zone_map.join('')}`
}

/**
 * Loops through the pricing plans and adds a unique title to each pricing plan and its pricings.
 */
const addUniqueTitleToPricingPlans = (pricingPlans: PricingPlan[], productId: string) => {
  return pricingPlans.map(pricingPlan => ({
    ...pricingPlan,
    title: generatePricingPlanTitle(pricingPlan, productId),
    pricings: pricingPlan.pricings.map(pricing => ({
      ...pricing,
      title: generatePricingPlanTitle(pricingPlan, productId)
    }))
  }))
}

interface ProductPricingGridProps {
  pricingPlans: PricingPlan[]
  zoneDefinition: ZoneDefinition
  product: Product
  handleOpenActivePeriod: () => void
  setProductPricingPlans: React.Dispatch<React.SetStateAction<PricingPlan[]>>
  activePeriod: Validity | null
  pricingGridType: PricingTabType
  editedPricingCells: RowType[]
  setEditedPricingCells: React.Dispatch<React.SetStateAction<RowType[]>>
  updatedPricingCells: RowType[]
  domicileClass?: string
}

function ProductPricingGrid({
  pricingPlans,
  zoneDefinition,
  product,
  handleOpenActivePeriod,
  setProductPricingPlans,
  activePeriod,
  pricingGridType,
  editedPricingCells,
  setEditedPricingCells,
  updatedPricingCells,
  domicileClass
}: Readonly<ProductPricingGridProps>) {
  const apiRef = useGridApiRef()
  const { markEdited } = useUnsavedChanges()
  const allZones = Array.from(new Set(zoneDefinition.zones.flat()))
  const [rows, setRows] = useState<RowType[]>([])
  const { copiedPricingPlans, setCopiedPricingPlans } = useContext(TariffContext)
  const isOriginalPricingPlan = activePeriod == null

  const defaultPricingPlans = getDefaultPricingPlans(pricingPlans)

  const [currentPricingPlans, setCurrentPricingPlans] = useState<PricingPlan[]>(defaultPricingPlans)

  const specialZonePricingPlans = pricingPlans.filter(
    pricingPlan =>
      // Filter by the current domicile class (or null if not set)
      matchPricingPlanByCurrentDomicileClass(pricingPlan) &&
      // Only include the pricing plans which belong to special_zones.
      zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
  )
  const [currentSpecialZonePricingPlans, setCurrentSpecialZonePricingPlans] =
    useState<PricingPlan[]>(specialZonePricingPlans)

  function getDefaultPricingPlans(pricingPlans: PricingPlan[]) {
    return pricingPlans.filter(
      pricingPlan =>
        // Filter by the current domicile class (or null if not set)
        matchPricingPlanByCurrentDomicileClass(pricingPlan) &&
        // Filter out the pricing plans which belong to special_zones.
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )
  }

  /** Matches pricing plan by the current domicile class or null if domicileClass is not set */
  function matchPricingPlanByCurrentDomicileClass(pricingPlan: PricingPlan) {
    const isDomicileClassMatch = domicileClass
      ? pricingPlan.domicile_class === domicileClass
      : pricingPlan.domicile_class === null
    return isDomicileClassMatch
  }

  // ROW_HEIGHT is multiplied by the number of zones + 1 to account for the title row.
  const dataGridContainerHeight = ROW_HEIGHT * (allZones.length + 1) + DATA_GRID_BORDER_WIDTH * 2

  const isExchangePeriodTab = pricingGridType === PricingTabType.EXCHANGE_PERIOD

  const gridValueFormatter = (value: number | string | null) => {
    if (value == null || value === '') {
      return ''
    }

    if (typeof value === 'string') {
      return value
    }

    if (isExchangePeriodTab) {
      return new Intl.NumberFormat('fi-FI', {
        style: 'unit',
        unit: 'minute'
      }).format(value)
    } else {
      return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(value)
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: '',
      width: TITLE_COL_WIDTH,
      type: 'string',
      sortable: false,
      headerAlign: 'center',
      align: 'center'
    },
    ...allZones.map(zoneId => ({
      field: zoneId,
      headerName: getTranslation(zoneDefinition.translations[zoneId], 'fi').value,
      width: COL_WIDTH,
      type: 'number' as const,
      editable: true,
      sortable: false,
      headerAlign: 'center' as const,
      flex: 1,
      valueFormatter: gridValueFormatter,
      cellClassName: (params: GridCellParams) => {
        if (params.value === undefined) {
          return 'MuiDataGrid-cell--disabled'
        }
        return ''
      }
    }))
  ]

  // Create rows and empty cells for each zoneDefinition
  // Row id is the first zone in zoneMap since it is the starting zone
  const createRows = () => {
    const newRows: RowType[] = []

    // The zoneDefinition.zones consists of all the possible zone combinations (zoneMaps).
    // Each zoneMap is an array of zoneIds, such as ['20', '21', '22']
    for (const zoneMap of zoneDefinition.zones) {
      const currentZone = zoneMap[0]
      const existingRow = newRows.find(row => row.id === currentZone)
      const row: RowType = existingRow ?? { id: '', title: '' }
      row.id = currentZone
      row.title = columns.find(column => column.field === currentZone)?.headerName ?? ''
      const lastZone = zoneMap[zoneMap.length - 1]
      // Cells are empty by default. Exchange period cells are disabled by default.
      row[lastZone] = isExchangePeriodTab ? undefined : ''

      if (!existingRow) {
        newRows.push(row)
      }
    }

    return newRows
  }

  const addExistingPricings = (newRows: RowType[]) => {
    // Add existing pricing plans to rows
    getDefaultPricingPlans(pricingPlans).forEach(pricingPlan => {
      const row = newRows.find(row => row.id === pricingPlan.zone_map[0])
      if (!row) return
      const targetZone = pricingPlan.zone_map[pricingPlan.zone_map.length - 1]
      row[targetZone] = pricingPlan.pricings[0].price
    })

    return newRows
  }

  const addExistingExchangePeriods = (newRows: RowType[]) => {
    // Add existing pricing plans to rows
    getDefaultPricingPlans(pricingPlans).forEach(pricingPlan => {
      const row = newRows.find(row => row.id === pricingPlan.zone_map[0])
      if (!row) return
      const targetZone = pricingPlan.zone_map[pricingPlan.zone_map.length - 1]
      if (pricingPlan.usage_validity_duration) {
        row[targetZone] = calculateISODurationLengthInMinutes(pricingPlan.usage_validity_duration)
      } else {
        row[targetZone] = ''
      }
    })

    return newRows
  }

  const resetRows = () => {
    let newRows = createRows()
    if (isExchangePeriodTab) {
      newRows = addExistingExchangePeriods(newRows)
    } else {
      newRows = addExistingPricings(newRows)
    }
    setRows(newRows)
  }

  useEffect(() => {
    resetRows()
    const updatedDefaultPricingPlans = getDefaultPricingPlans(pricingPlans)
    setCurrentPricingPlans(updatedDefaultPricingPlans)
  }, [pricingPlans])

  /**
   * Updates an existing pricing plan with the latest price.
   * If no pricing plan exists, creates a new one.
   * If the price is empty and a pricing plan exists, removes the existing pricing plan.
   * @param {string[]} zoneMap - The zone map to update or create a pricing plan for.
   * @param {number | string | null | undefined} price - The latest price for the zone map.
   * @param {PricingPlan[]} currentPricingPlans - The array of current pricing plans.
   * @returns {PricingPlan[]} The updated array of pricing plans.
   */
  const updateOrCreatePricingPlan = (
    zoneMap: string[],
    price: number | string | null | undefined,
    currentPricingPlans: PricingPlan[]
  ): PricingPlan[] => {
    const existingPricingPlanIndex = findExistingPricingPlanIndex(currentPricingPlans, zoneMap)

    if (price === '' || price == null) {
      if (existingPricingPlanIndex > -1) {
        // Remove the existing pricing plan if the price is empty
        currentPricingPlans.splice(existingPricingPlanIndex, 1)
      }
      // If the price is empty, there is no need to create a new pricing plan
      return currentPricingPlans
    }

    if (existingPricingPlanIndex > -1) {
      const existingPricingPlanClone = {
        ...structuredClone(currentPricingPlans[existingPricingPlanIndex]),
        title: generatePricingPlanTitle(currentPricingPlans[existingPricingPlanIndex], product.id)
      }
      if (existingPricingPlanClone.pricings.length > 0) {
        existingPricingPlanClone.pricings[0].price = price as number
        existingPricingPlanClone.pricings[0].title = generatePricingPlanTitle(
          existingPricingPlanClone,
          product.id
        )
      } else {
        existingPricingPlanClone.pricings[0] = {
          price: price as number,
          currency: 'EUR',
          vat_included: true,
          title: generatePricingPlanTitle(existingPricingPlanClone, product.id),
          description: ''
        }
      }

      currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
    } else {
      const newPricingPlanData = {
        active_period: activePeriod,
        visible_period: null,
        domicile_class: domicileClass ?? null,
        custom_fields: {},
        description: '',
        usage_validity_duration: null,
        zone_map: zoneMap
      } as PricingPlan
      currentPricingPlans.push({
        ...newPricingPlanData,
        pricings: [
          {
            price: price as number,
            currency: 'EUR',
            vat_included: true,
            title: generatePricingPlanTitle(newPricingPlanData, product.id),
            description: ''
          }
        ],
        title: generatePricingPlanTitle(newPricingPlanData, product.id)
      })
    }

    return currentPricingPlans
  }

  const updateOrCreatePricingExchangePeriod = (
    zoneMap: string[],
    validityLength: number | string | null | undefined,
    currentPricingPlans: PricingPlan[]
  ): PricingPlan[] => {
    const existingPricingPlanIndex = findExistingPricingPlanIndex(currentPricingPlans, zoneMap)

    if (validityLength === '' || validityLength == null) {
      if (existingPricingPlanIndex > -1) {
        // Don't remove the entire plan, just update the validity
        const existingPricingPlanClone = structuredClone(
          currentPricingPlans[existingPricingPlanIndex]
        )
        existingPricingPlanClone.usage_validity_duration = null
        currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
      }
      return currentPricingPlans
    }

    if (existingPricingPlanIndex > -1) {
      const existingPricingPlanClone = structuredClone(
        currentPricingPlans[existingPricingPlanIndex]
      )
      existingPricingPlanClone.usage_validity_duration = createDurationMap(
        validityLength as number
      ).minutes
      currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
    }

    return currentPricingPlans
  }

  const isExtensionTicket = product.product_group === EXTENSION_TICKET_GROUP_CODE

  const filterSpecialZonePricingPlans = (
    zoneDefinition: ZoneDefinition,
    pricingPlan: PricingPlan
  ) => {
    return !zoneDefinition.special_zones.some(
      zone => pricingPlan.zone_map.join('') === zone.join('')
    )
  }

  /**
   * Handles changing the price of a special zone pricing plan or creating a new one if it does not exist.
   *
   * @param zoneMap
   * @param price
   * @param specialZonePricingPlans
   */
  const handleSpecialZonePricingPlanPriceChange = (
    zoneMap: string[],
    price: string,
    specialZonePricingPlans: PricingPlan[]
  ) => {
    let currentSpecialZonePricingPlans: PricingPlan[] = [...specialZonePricingPlans]

    if (isExchangePeriodTab) {
      // Update or create pricing plan for exchange period
      currentSpecialZonePricingPlans = updateOrCreatePricingExchangePeriod(
        zoneMap,
        price,
        currentSpecialZonePricingPlans
      )
    } else {
      currentSpecialZonePricingPlans = updateOrCreatePricingPlan(
        zoneMap,
        price,
        currentSpecialZonePricingPlans
      )
    }

    setProductPricingPlans(prevState => {
      let otherPricingPlans: PricingPlan[] = []
      // Filter out the pricing plans that are not in special zones
      if (isOriginalPricingPlan) {
        otherPricingPlans = prevState.filter(
          pricingPlan =>
            pricingPlan.active_period !== activePeriod ||
            pricingPlan.domicile_class !== (domicileClass ?? null) ||
            filterSpecialZonePricingPlans(zoneDefinition, pricingPlan)
        )
      } else {
        otherPricingPlans = prevState.filter(
          pricingPlan =>
            pricingPlan.active_period?.title !== activePeriod.title ||
            pricingPlan.domicile_class !== (domicileClass ?? null) ||
            filterSpecialZonePricingPlans(zoneDefinition, pricingPlan)
        )
      }

      markEdited()
      // Combine the updated special zone pricing plans with the other pricing plans
      return [...otherPricingPlans, ...currentSpecialZonePricingPlans]
    })
  }

  /**
   * Finds the index of an existing pricing plan for the given zone map.
   * @param pricingPlans
   * @param {Array} zoneMap - Array of zoneIds.
   * @returns {Number} Index of existing pricing plan, or -1 if not found.
   */
  const findExistingPricingPlanIndex = (pricingPlans: PricingPlan[], zoneMap: string[]): number => {
    return pricingPlans.findIndex(
      pricingPlan =>
        zoneMap.length === pricingPlan.zone_map.length &&
        zoneMap.every((zone, index) => zone === pricingPlan.zone_map[index]) &&
        pricingPlan.domicile_class === (domicileClass ?? null)
    )
  }

  const handleCopyPricingPlans = () => {
    setCopiedPricingPlans([...currentPricingPlans, ...currentSpecialZonePricingPlans])
    SnackbarUtils.info(t`Pricing plans copied.`)
  }

  const handlePastePricingPlans = () => {
    if (copiedPricingPlans) {
      // Set domicile_class to match current domicileClass
      const pricingPlansWithCorrectDomicileClass = copiedPricingPlans.map(plan => ({
        ...plan,
        domicile_class: domicileClass ?? null
      }))

      const updatedPricingPlansWithNewTitle = addUniqueTitleToPricingPlans(
        pricingPlansWithCorrectDomicileClass,
        product.id
      )

      pastePricingPlansWithoutSpecialZones(updatedPricingPlansWithNewTitle)

      if (isExtensionTicket) {
        pastePricingPlansWithSpecialZones(pricingPlansWithCorrectDomicileClass)
      }
      markEdited()
    }
  }

  const pastePricingPlansWithoutSpecialZones = (updatedPricingPlansWithNewTitle: PricingPlan[]) => {
    // Filter out the pricing plans that are not in special zones.
    // Special zones are only copied for extension tickets.
    const pricingPlansWithoutSpecialZones = updatedPricingPlansWithNewTitle.filter(
      pricingPlan =>
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )

    setCurrentPricingPlans(pricingPlansWithoutSpecialZones)
    // Replace only the pricing plans which have same active period and same domicile class
    // as the copied pricing plans
    setProductPricingPlans(prevState => [
      ...prevState.filter(
        plan =>
          plan.active_period?.title !== activePeriod?.title ||
          plan.domicile_class !== (domicileClass ?? null)
      ),
      ...pricingPlansWithoutSpecialZones.filter(
        pricingPlan =>
          pricingPlan.active_period === activePeriod &&
          pricingPlan.domicile_class === (domicileClass ?? null)
      )
    ])
  }

  const pastePricingPlansWithSpecialZones = (
    pricingPlansWithCorrectDomicileClass: PricingPlan[]
  ) => {
    // Filter out the pricing plans that are in special zones.
    // When copying pricing plans for an extension ticket,
    // we also want to copy the special zones pricing plans
    const pricingPlansWithSpecialZones = pricingPlansWithCorrectDomicileClass.filter(pricingPlan =>
      zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )

    const updatedSpecialZonePricingPlansWithNewTitle = addUniqueTitleToPricingPlans(
      pricingPlansWithSpecialZones,
      product.id
    )

    setCurrentSpecialZonePricingPlans(updatedSpecialZonePricingPlansWithNewTitle)
    setProductPricingPlans(prevState => [
      ...prevState,
      ...updatedSpecialZonePricingPlansWithNewTitle
    ])
  }

  /**
   * Handles the update of a pricing plan cell.
   * This function updates the pricing plans based on the modified row.
   * It finds all zone maps that start with the modified row's ID and updates each relevant zone map
   * with the new value. If the updated pricing plans are the same as the original pricing plans,
   * it does not update the parent component.
   * Otherwise, it updates the parent component with the new pricing plans.
   */
  const handlePricingPlanCellUpdate = (newRow: RowType, newRows: RowType[]) => {
    // Update pricing plans based on the modified row
    let currentPricingPlansClone: PricingPlan[] = [...currentPricingPlans]

    // Find all zone maps that start with this row's ID
    const relevantZoneMaps = zoneDefinition.zones.filter(zoneMap => zoneMap[0] === newRow.id)

    // Update each relevant zone map with the new value
    for (const zoneMap of relevantZoneMaps) {
      const lastZone = zoneMap[zoneMap.length - 1]
      const lastZonePriceValue = newRow[lastZone]

      if (isExchangePeriodTab) {
        // Update or create pricing plan for exchange period
        currentPricingPlansClone = updateOrCreatePricingExchangePeriod(
          zoneMap,
          lastZonePriceValue,
          currentPricingPlansClone
        )
      } else {
        currentPricingPlansClone = updateOrCreatePricingPlan(
          zoneMap,
          lastZonePriceValue,
          currentPricingPlansClone
        )
      }
    }

    // Update the current pricing plans state
    setCurrentPricingPlans(currentPricingPlansClone)

    const updatedPricingPlans = [...currentPricingPlansClone, ...currentSpecialZonePricingPlans]

    // Check if the updated pricing plans are the same as the original pricing plans
    // If they are the same, we do not need to update the parent component
    if (JSON.stringify(updatedPricingPlans) === JSON.stringify(pricingPlans)) {
      return newRows
    }

    // Update the parent component and append values to original state
    // Replace the pricing plans in the parent component which have
    // the same active period title and same domicile class
    setProductPricingPlans(prevState => {
      if (isOriginalPricingPlan) {
        // Filter out the pricing plans which are not updated
        const originalPricingPlans = prevState.filter(
          plan =>
            plan.active_period !== activePeriod || plan.domicile_class !== (domicileClass ?? null)
        )
        return [...updatedPricingPlans, ...originalPricingPlans]
      } else {
        // Filter out the pricing plans which are not updated
        const originalPricingPlans = prevState.filter(
          plan =>
            plan.active_period?.title !== activePeriod.title ||
            plan.domicile_class !== (domicileClass ?? null)
        )
        return [...updatedPricingPlans, ...originalPricingPlans]
      }
    })
    markEdited()
  }

  const getCellClassName = (params: GridCellParams) => {
    if (editedPricingCells.length > 0) {
      return editedPricingCells.some(
        cell =>
          cell.id === params.id &&
          cell.field === params.field &&
          cell.gridId === apiRef.current.instanceId.id
      )
        ? IsCellEditedClassName
        : ''
    }

    return updatedPricingCells.some(
      cell =>
        cell.id === params.id &&
        cell.field === params.field &&
        cell.gridId === apiRef.current.instanceId.id
    )
      ? IsCellUpdatedClassName
      : ''
  }

  const isCellEditable = (params: GridCellParams) => {
    return params.value !== undefined && params.value !== '-' && IS_TARIFF_EDIT_ALLOWED
  }

  const updateEditedPricingPlanCells = (newRow: RowType, oldRow: RowType) => {
    const newEditedCell = {
      id: newRow.id,
      // Find which field has been edited by comparing all properties between newRow and oldRow
      field: Object.keys(newRow).find(key => newRow[key] !== oldRow[key]) || '',
      title: newRow.title,
      gridId: apiRef.current.instanceId.id
    } as RowType

    if (
      newEditedCell.field &&
      !editedPricingCells.some(
        existingCell =>
          existingCell.id === newEditedCell.id &&
          existingCell.field === newEditedCell.field &&
          existingCell.gridId === newEditedCell.gridId
      )
    ) {
      setEditedPricingCells((prevState: RowType[]) => [...prevState, newEditedCell])
    }
  }

  return (
    <Box>
      <Box sx={{ height: dataGridContainerHeight }}>
        <StyledDataGrid
          apiRef={apiRef}
          hideFooter
          disableColumnFilter={true}
          rowHeight={ROW_HEIGHT}
          columnHeaderHeight={ROW_HEIGHT}
          showCellVerticalBorder
          showColumnVerticalBorder
          processRowUpdate={(newRow, oldRow) => {
            setRows(prevRows => {
              const newRows = [...prevRows]
              const rowIndex = newRows.findIndex(row => row.id === newRow.id)
              newRows[rowIndex] = newRow as RowType

              handlePricingPlanCellUpdate(newRow as RowType, newRows)
              return newRows
            })

            updateEditedPricingPlanCells(newRow as RowType, oldRow as RowType)
            return newRow
          }}
          getCellClassName={getCellClassName}
          disableColumnMenu={true}
          isCellEditable={isCellEditable}
          onCellClick={(params: GridCellParams) => {
            if (params.cellMode === 'edit') return
            // Only start edit mode if the cell is editable
            if (isCellEditable(params)) {
              apiRef.current.startCellEditMode({
                id: params.id,
                field: params.field
              })
            }
          }}
          disableRowSelectionOnClick={true}
          columns={columns}
          rows={rows}
          slots={{
            noRowsOverlay: () => CustomNoRowsOverlay(allZones)
          }}
        />
      </Box>
      {isExtensionTicket && (
        <SpecialZonePricing
          zoneDefinition={zoneDefinition}
          product={product}
          pricingPlans={pricingPlans}
          activePeriod={activePeriod}
          pricingGridType={pricingGridType}
          domicileClass={domicileClass}
          currentSpecialZonePricingPlans={currentSpecialZonePricingPlans}
          handleSpecialZonePricingPlanPriceChange={handleSpecialZonePricingPlanPriceChange}
        />
      )}
      {IS_TARIFF_EDIT_ALLOWED && isOriginalPricingPlan && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            columnGap: 2
          }}
        >
          <Button
            size="medium"
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleOpenActivePeriod}
          >
            {t`Add pricing exception`}
          </Button>
          <CopyPasteProductPricing
            onCopyPricingPlans={handleCopyPricingPlans}
            onPastePricingPlans={handlePastePricingPlans}
            copiedPricingPlansExist={copiedPricingPlans !== undefined}
            domicileClass={domicileClass}
          />
        </Box>
      )}
    </Box>
  )
}

export default ProductPricingGrid
